import { observer } from "mobx-react"
import React, { useState } from "react"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { useTranslation } from "react-i18next"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import { FormInput } from "components/ProjectNonSpecificComponents/Elements/FormElements/FormInput"
import HorizontalFormField from "../HorizontalFormField"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import {
	IAdditional,
	ISettings,
} from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import { lostOrFinishedAdminSession } from "components/ProjectSpecificComponents/API/Functions/lostOrFinishedAdminSession"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import { useHistory, useLocation } from "react-router-dom"
import ROUTES from "components/ProjectSpecificComponents/Constants/ROUTES"
import IconButton from "components/ProjectSpecificComponents/Elements/Global/atoms/Buttons/IconButton"

interface Props {}

export const UserFormWelcomeText = observer(({}: Props) => {
	const { t, i18n } = useTranslation()
	const { isMobile } = useScreenType()
	const store = useStore()
	const formStore = useFormStore()
	const history = useHistory()
	const location = useLocation()
	const { settingsLanguage } = formStore
	const [editTextUserSide, setEditTextUserSide] = useState(false)

	const { userHasAdminAccess } = store
	// ! TECHNICAL DEBT: 3184, replace this with dynamic key ([parsedkey])
	let isENAdminSide = settingsLanguage === "en"
	let isENUserSide = i18n.language === "en"
	const { welcomeTextLV, welcomeTextEN } = formStore.settingsData.additionalInfo

	const isUserSide = location.pathname === ROUTES.USER
	const welcomeText = isUserSide
		? isENUserSide
			? welcomeTextEN
			: welcomeTextLV
		: isENAdminSide
		? welcomeTextEN
		: welcomeTextLV
	const charLimit = 80
	// admin only can open input and save after close
	const toggleInput = () => {
		if (!userHasAdminAccess) return
		if (editTextUserSide) saveAdditionalFields()
		setEditTextUserSide((isSelectOpen: boolean) => !isSelectOpen)
	}

	const onChangeText = (text: string) => {
		let _text = text
		if (charLimit && text.length > charLimit) {
			_text = text.substr(0, charLimit)
		}
		formStore.mergeUserFormWelcomeTextData({
			[`welcomeText${isUserSide ? (isENUserSide ? "EN" : "LV") : isENAdminSide ? "EN" : "LV"}`]:
				_text,
		})
	}

	const saveAdditionalFields = async () => {
		const { ok, data, statusCode } = await AppRequests("Setting").post<
			Partial<IAdditional>,
			ISettings
		>("save-additional-field", formStore.settingsData.additionalInfo)

		if (ok && data) {
			formStore.mergeSettingsDataSafely(data)
			store.setInfoModalConfig({
				type: "info",
				titleKey: "data-saving.additional-success",
				showDuration: 1000,
			})
		} else if (statusCode == 401) {
			lostOrFinishedAdminSession(history, store)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed",
			})
		}
	}

	return (
		<>
			{isUserSide && (
				<>
					{/* User side view and toggle for admin */}
					{!editTextUserSide && (
						<HyperDiv className="center-x-y">
							{welcomeText && (
								<HyperDiv
									style={{ marginTop: 30, textAlign: "center" }}
									width={isMobile ? "80%" : "60%"}
									onClick={toggleInput}
									direction="row"
									centered
								>
									<p className="pr-2">{welcomeText}</p>
									{userHasAdminAccess && <IconButton iconKey="pencil" />}
								</HyperDiv>
							)}

							{!welcomeText && userHasAdminAccess && (
								<HyperDiv
									style={{ textAlign: "center" }}
									width={isMobile ? "80%" : "60%"}
									centered
									onClick={toggleInput}
									className="cursor-pointer"
								>
									<p className="pr-2">{t("rawText.add-welcome-text")}</p>
									<IconButton iconKey="pencil" />
								</HyperDiv>
							)}
						</HyperDiv>
					)}

					{editTextUserSide && (
						<HyperDiv style={{ marginTop: 30, textAlignLast: "center" }} onBlur={toggleInput}>
							<FormInput
								autoFocus
								charLimit={charLimit}
								value={welcomeText}
								onValueChange={(text) => onChangeText(text)}
							/>
						</HyperDiv>
					)}
				</>
			)}
			{!isUserSide && (
				<>
					{/* Admin side setting modal */}
					<HyperDiv direction="column" padding=".5rem 0" spaceBetweenElems="2.25rem">
						<HorizontalFormField
							containerStyle={{ padding: "8px 16px" }}
							label={t(`forms.field-label.welcome-text`)}
							centerSpaceMultiplier={0.5}
							tooltipBelowInputField
						>
							<FormInput
								wide
								charLimit={charLimit}
								value={welcomeText}
								onValueChange={(text) => onChangeText(text)}
							/>
							<Whitespace h x="1" />
						</HorizontalFormField>
					</HyperDiv>
				</>
			)}
		</>
	)
})

export default UserFormWelcomeText
